import React from "react";
import { Link } from "react-router-dom";
import { canonical } from "../data/business";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { services } from "../data/services";
import { locations } from "../data/locations";
import { projects } from "../data/projects";
import { blogs } from "../data/blogs";

export default function SitemapPage() {
  return (
    <div className="page-enter">
      <Seo
        title="Sitemap — Last Cut Landscaping"
        description="Browse every page on lastcutlandscaping.co.uk — services, location coverage, completed projects and articles."
        canonical={canonical("/sitemap")}
      />
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20" data-testid="html-sitemap">
        <Breadcrumbs items={[{ label: "Sitemap" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Sitemap</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">Every page on Last Cut Landscaping at a glance — useful if you can't find what you're looking for.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          <Group title="Main pages">
            <SitemapLink to="/">Home</SitemapLink>
            <SitemapLink to="/services">Services</SitemapLink>
            <SitemapLink to="/locations">Areas we cover</SitemapLink>
            <SitemapLink to="/projects">Projects</SitemapLink>
            <SitemapLink to="/blog">Blog</SitemapLink>
            <SitemapLink to="/about">About</SitemapLink>
            <SitemapLink to="/contact">Contact</SitemapLink>
            <SitemapLink to="/quote">Free quote</SitemapLink>
            <SitemapLink to="/faq">FAQ</SitemapLink>
            <SitemapLink to="/privacy">Privacy</SitemapLink>
            <SitemapLink to="/terms">Terms</SitemapLink>
          </Group>

          <Group title="Services">
            {services.map((s) => (
              <SitemapLink key={s.slug} to={`/services/${s.slug}`}>{s.name}</SitemapLink>
            ))}
          </Group>

          <Group title="Areas covered">
            {locations.map((l) => (
              <SitemapLink key={l.slug} to={`/locations/${l.slug}`}>{l.name}</SitemapLink>
            ))}
          </Group>

          {services.map((s) => (
            <Group key={s.slug} title={`${s.name} by area`}>
              {locations.map((l) => (
                <SitemapLink key={`${s.slug}-${l.slug}`} to={`/${s.slug}/${l.slug}`}>
                  {s.name.replace(/\s+Installation$/i, "")} in {l.name}
                </SitemapLink>
              ))}
            </Group>
          ))}

          {projects.length > 0 && (
            <Group title="Projects">
              {projects.map((p) => (
                <SitemapLink key={p.slug} to={`/projects/${p.slug}`}>{p.title}</SitemapLink>
              ))}
            </Group>
          )}

          {blogs.length > 0 && (
            <Group title="Articles">
              {blogs.map((b) => (
                <SitemapLink key={b.slug} to={`/blog/${b.slug}`}>{b.title}</SitemapLink>
              ))}
            </Group>
          )}
        </div>

        <div className="mt-16 p-6 lc-card">
          <p className="text-sm text-dark/65">
            Looking for the machine-readable XML sitemap?{" "}
            <a href="/sitemap.xml" className="text-[color:var(--lc-green)] font-semibold underline" data-testid="xml-sitemap-link">
              View sitemap.xml
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}

const Group = ({ title, children }) => (
  <div>
    <h2 className="font-display text-xl text-dark mb-3 border-b border-lc pb-2">{title}</h2>
    <ul className="space-y-1.5">{children}</ul>
  </div>
);

const SitemapLink = ({ to, children }) => (
  <li>
    <Link
      to={to}
      className="text-[15px] text-dark/80 hover:text-[color:var(--lc-green)] hover:underline transition"
      data-testid={`sitemap-link-${to.replace(/\//g, "-").replace(/^-/, "")}`}
    >
      {children}
    </Link>
  </li>
);
